// ── RMA'S PRODUCTION RECORD TYPE CODES ───────────────────────────────────────
//
// A production report does not just carry bushels; it carries, per unit, WHAT KIND OF
// RECORD the bushels came from. The APH Database prints a `Prod. Rec. Type` column for
// exactly that, and until now nothing in this app knew the vocabulary.
//
// Kevin, 2026-09-30, describing the evidence a production report is built from: *"it might
// be appraisals plus bin measurement or appraisals plus yield data from JD or appraisals
// and scale tickets or no appraisals and only scale tickets or only bin measurement or
// basically any combination of production records."*
//
// ⚠⚠ THAT IS NOT A FREE-FORM DESIGN PROBLEM. RMA ENUMERATES THE RECORD TYPES, and the
// complete list is PRINTED ON A DOCUMENT IN OUR OWN STORAGE — page 9 of the NAU "Schedule
// of Insurance Acreage Report and Production Report", read verbatim 2026-10-01 out of
// `SHIPLEY THOMAS PA-942-6108100-26 …`. Every string below is the document's own wording.
// Nothing here is inferred, and a code this app has not seen must render RAW rather than be
// guessed at — the same convention the claim stage codes and the corn type codes follow.
//
// So the shape of a production-report review surface falls out of the form: its rows are
// (unit x record type), "all acres accounted for" means every insured acre carries a code,
// and the fill stamps a figure and its code.

"use strict";

// ⚠ THE COLUMN HEADINGS ARE `Harvested` (x 308) AND `Unharvested` (x 446), AND THE BOTTOM
// THREE ENTRIES DO NOT CLEANLY BELONG TO EITHER. Read off the page, the left column runs
// A..H and then continues R, T, `#` BELOW where the right column's list ends — so by
// position R/T/# are "harvested", while T's own text reads "No Production. Unharvested."
// and R describes a pre-harvest appraisal. Either the left column simply wraps past the
// right one, or those three sit outside the split.
//
// I cannot tell which from one document, so `group` is only asserted where the page is
// unambiguous and is `null` for those three. A guessed grouping would be an invented fact
// about a federal form, and nothing in this app needs it: the code itself is what the form
// asks for.
const CODES = Object.freeze({
  // ── the left column, under "Harvested" ──
  A: { label: "Sold/Commercial Storage", group: "harvested" },
  B: { label: "Farm Stored by Insured", group: "harvested" },
  C: { label: "Pick/Daily Sales Records", group: "harvested" },
  D: { label: "Automated Yield Monitoring System", group: "harvested" },
  E: { label: "Farm Stored Measured by Authorized Representative", group: "harvested" },
  F: { label: "Livestock Feeding Records", group: "harvested" },
  G: { label: "Field Harvest Record", group: "harvested" },
  H: { label: "Other", group: "harvested" },

  // ── the right column, under "Unharvested" ──
  I: { label: "Destroyed (ARPI only)", group: "unharvested" },
  J: { label: "Put to another use (ARPI only)", group: "unharvested" },
  K: { label: "Appraised by AIP (ARPI only)", group: "unharvested" },
  L: { label: "Unreported production (ARPI only)", group: "unharvested" },
  M: { label: "Claim for Indemnity", group: "unharvested" },
  N: { label: "Appraisal (Non-Loss)", group: "unharvested" },
  O: { label: "UUF or Third-Party Damage", group: "unharvested" },
  P: { label: "Unharvested with harvest incomplete (ARPI only)", group: "unharvested" },
  Q: { label: "Zero Production", group: "unharvested" },
  Z: { label: "Zero Planted Acres", group: "unharvested" },

  // ── printed in the left column BELOW the right column's list; see the note above ──
  R: { label: "Pre-Harvest Appraisal Allocated Production", group: null },
  T: { label: "No Production. Unharvested.", group: null },
  "#": { label: "Trees or Vines", group: null },
});

// The document's own source, carried with the data so a reader can go and check it.
const SOURCE = Object.freeze({
  document: "NAU Schedule of Insurance Acreage Report and Production Report",
  page: 9,
  heading: "Production Record Type Code",
  read_on: "2026-10-01",
  read_from: "SHIPLEY THOMAS PA-942-6108100-26",
});

/** A code's label, or null. NEVER a guess — an unknown code renders raw. */
function recordTypeLabel(code) {
  const k = String(code == null ? "" : code).trim().toUpperCase();
  return (CODES[k] && CODES[k].label) || null;
}

/** `A — Sold/Commercial Storage`, or the bare code plus a named absence. */
function recordTypeText(code) {
  const k = String(code == null ? "" : code).trim().toUpperCase();
  if (!k) return "no record type stated";
  const hit = CODES[k];
  return hit ? `${k} — ${hit.label}` : `code ${k} (meaning not established)`;
}

/** Is this a code RMA publishes? Used to decide raw-render, never to drop a row. */
const isKnownRecordType = (code) => !!CODES[String(code == null ? "" : code).trim().toUpperCase()];

// ── WHAT WE HOLD, AND WHICH CODE IT IS ───────────────────────────────────────
//
// ⚠ THIS MAPS A SOURCE OF EVIDENCE TO A CODE. IT DOES NOT DECIDE ONE. Two of these are
// genuinely ambiguous and the ambiguity is a question for the grower, not for a default:
//
//   B vs E — "Farm Stored by Insured" vs "Farm Stored Measured by Authorized
//            Representative". A bin measurement is one or the other depending on WHO
//            measured it, which is not in any table here. `needsHuman` says so.
//   N vs K vs R — an appraisal is "Appraisal (Non-Loss)" when there is no claim, "Appraised
//            by AIP" on an ARPI policy, and "Pre-Harvest Appraisal Allocated Production"
//            when its production is allocated across acres. Our `silage_appraisals` rows do
//            not state which.
//
// Where a channel maps to exactly one code and nothing else, `code` is set. Where it does
// not, `candidates` lists them and `needsHuman` is true. A surface that silently picked the
// first candidate would be inventing a statement on a federal form.
const EVIDENCE = Object.freeze({
  scale_tickets: {
    label: "Scale tickets", table: "grain_delivery_tickets",
    code: "A", needsHuman: false,
    note: "sold or delivered to an elevator — the elevator's own weight record",
  },
  bin_measurement: {
    label: "On-farm stored (bin measurement)", table: "claim_check_inputs.stored_bu",
    code: null, candidates: ["B", "E"], needsHuman: true,
    note: "B if the insured measured it, E if an authorized representative did — nothing we hold says which",
  },
  jd_yield: {
    label: "John Deere yield monitor", table: "jd_field_ftf (metric='yield')",
    code: "D", needsHuman: false,
    note: "RMA names this one exactly: Automated Yield Monitoring System",
  },
  appraisal: {
    label: "Adjuster appraisal", table: "silage_appraisals",
    code: null, candidates: ["N", "K", "R"], needsHuman: true,
    note: "N with no claim, K on ARPI, R when the appraised production is allocated across acres",
  },
  fed_on_farm: {
    label: "Fed on farm", table: "claim_check_inputs.fed_bu",
    code: "F", needsHuman: false,
    note: "Livestock Feeding Records",
  },
  claim: {
    label: "Open or settled claim", table: "indemnities",
    code: "M", needsHuman: false,
    note: "Claim for Indemnity — the APH carries this for the life of the database",
  },
  zero_planted: {
    label: "Nothing planted", table: "production_history (record_type Z)",
    code: "Z", needsHuman: false,
    note: "Zero Planted Acres — and NOT the same statement as zero production (Q)",
  },
});

/**
 * codeForEvidence(kind) -> { code, candidates, needsHuman, why } | null
 *
 * ⚠ RETURNS A REFUSAL RATHER THAN A DEFAULT where the evidence does not determine the
 * code. A production report is certified by the grower; a code we chose for them is a
 * statement they did not make.
 */
function codeForEvidence(kind) {
  const e = EVIDENCE[String(kind || "").trim()];
  if (!e) return null;
  return {
    code: e.code || null,
    candidates: e.code ? [e.code] : (e.candidates || []),
    needsHuman: !!e.needsHuman,
    why: e.note,
  };
}

// ⚠ `Q` AND `Z` ARE DIFFERENT STATEMENTS AND MUST NEVER BE FOLDED. "Zero Production" is a
// crop that was planted and yielded nothing — a total loss, the largest indemnity a policy
// can pay. "Zero Planted Acres" is ground that was never planted, which leaves the APH
// database entirely. CLAUDE.md records the same distinction on the acreage side: a county
// with no entry read as 0 is a total loss on that county.
const isZeroProduction = (code) => String(code || "").trim().toUpperCase() === "Q";
const isZeroPlanted = (code) => String(code || "").trim().toUpperCase() === "Z";

// A `Z` row leaves the APH average; a T or a PP row does NOT. src/App.jsx records the
// measurement: "RMA counts a T (transitional) or PP record as one of the ten, and only Z
// (Zero Planted Acres) leaves the database. Verified on Kennedy Farms' CY2027 APH printout:
// unit 0009 is 70T, 70T, 63, 43 and RMA approves 62 = 61.5 rounded, dividing by FOUR."
const countsInAphAverage = (code) => !isZeroPlanted(code);

module.exports = {
  CODES, SOURCE, EVIDENCE,
  recordTypeLabel, recordTypeText, isKnownRecordType,
  codeForEvidence,
  isZeroProduction, isZeroPlanted, countsInAphAverage,
};
